import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, CloudSun, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const LEAGUES = ["NBA", "NHL", "NFL", "MLB"];

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function TeamRow({ t }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      {t?.logo && <img src={t.logo} alt="" className="h-6 w-6 object-contain shrink-0" />}
      <span className="font-heading font-semibold text-slate-100 truncate">{t?.name || t?.abbr}</span>
      {t?.record && <span className="text-[10px] font-mono text-slate-500 shrink-0">{t.record}</span>}
      {t?.score != null && t?.score !== "" && <span className="ml-auto font-mono text-slate-300">{t.score}</span>}
    </div>
  );
}

function GameCard({ g }) {
  return (
    <Link to={`/games/${g.league}/${g.id}`} data-testid={`game-card-${g.id}`}
      className="block rounded-xl bg-[#0F1420] border border-[#232D42] p-4 hover:border-[#00E5A3]/40 transition-colors">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge className="bg-[#151B2B] text-slate-300 border border-[#232D42] text-[10px] font-mono">{g.league}</Badge>
          <span className="text-[11px] font-mono text-slate-500 flex items-center gap-1">
            <CalendarDays size={11} /> {fmtDate(g.date)}
          </span>
        </div>
        {g.status_state === "in" && (
          <Badge className="bg-[#FF6B6B]/15 text-[#FF6B6B] border-0 text-[10px] font-mono animate-pulse">LIVE</Badge>
        )}
        {g.status_state === "post" && (
          <span className="text-[10px] font-mono text-slate-500">{g.status_detail}</span>
        )}
      </div>
      <div className="space-y-2">
        <TeamRow t={g.away} />
        <TeamRow t={g.home} />
      </div>
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#232D42] text-[11px] font-mono text-slate-500">
        <div className="flex items-center gap-3 min-w-0">
          {g.venue?.name && <span className="flex items-center gap-1 truncate"><MapPin size={11} /> {g.venue.name}</span>}
          {g.weather?.temperature != null && g.venue?.indoor === false && (
            <span className="flex items-center gap-1 text-[#38BDF8] shrink-0">
              <CloudSun size={11} /> {g.weather.temperature}° {g.weather.conditionId}
            </span>
          )}
        </div>
        <span className="flex items-center gap-1 text-[#00E5A3] shrink-0">
          {g.odds?.details || "details"} <ChevronRight size={12} />
        </span>
      </div>
    </Link>
  );
}

export default function Games() {
  const [league, setLeague] = useState("NHL");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/games?league=${league}`)
      .then((r) => setGames(r.data.games || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [league]);

  return (
    <div data-testid="games-page" className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-100 mb-1">Games</h1>
      <p className="text-sm text-slate-400 mb-4 max-w-2xl">
        Every matchup with the info that moves it — injuries, starting goalies, recent form, weather and lines.
        Tap a game for the full breakdown.
      </p>
      <div className="flex items-center gap-2 mb-5">
        {LEAGUES.map((lg) => (
          <button key={lg} data-testid={`games-league-${lg}`} onClick={() => setLeague(lg)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              league === lg ? "bg-[#00E5A3] text-[#04140F]" : "bg-[#151B2B] text-slate-300 hover:bg-[#1C2438]"}`}>
            {lg}
          </button>
        ))}
      </div>
      {loading && games.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading games…</div>
      ) : games.length === 0 ? (
        <div data-testid="games-empty" className="text-center py-16 text-slate-500 text-sm">
          No {league} games on the schedule right now. They appear as the league posts its slate.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {games.map((g) => <GameCard key={g.id} g={g} />)}
        </div>
      )}
    </div>
  );
}
